import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import { reportBackendError, reportBackendOk } from './backendHealth';

const API_BASE_URL: string =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  'http://localhost:8000';

const client: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach Authorization header when a token is present in localStorage
client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('access_token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401, clear the stale token and announce it so the app can return the
// user to the login/home gate. We do NOT redirect via `window.location`
// here: this app has no `/login` route (`main.tsx` mounts `<Dashboard />`
// directly, no router), so the old `window.location.href = '/login'` hit
// Vite's SPA fallback, re-mounted the dashboard, and looped 401/redirect.
// Instead we dispatch an `auth:unauthorized` event; `App.tsx` listens and
// flips back to the auth gate (React state, no navigation → no loop).
export const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized';
client.interceptors.response.use(
  (response) => {
    // A successful round-trip means the backend is reachable again.
    reportBackendOk();
    return response;
  },
  (error: AxiosError) => {
    const status = error.response?.status;
    if (status === 401) {
      localStorage.removeItem('access_token');
      // Tell the app the session is gone so it can show the login/home gate.
      // Fires on both boot (invalid stored token → /auth/me 401) and
      // mid-session expiry (any protected request 401s). Idempotent for the
      // app — landing on the gate makes no further protected requests.
      window.dispatchEvent(new Event(AUTH_UNAUTHORIZED_EVENT));
    }
    // Connectivity errors are network failures (no response) or 5xx.
    // 401 and ordinary 4xx are auth/app concerns, not backend outages.
    if (!error.response) {
      reportBackendError(`Backend unreachable at ${client.defaults.baseURL}`);
    } else if (status && status >= 500) {
      reportBackendError(`Backend error ${status} — the server is having trouble`);
    }
    return Promise.reject(error);
  }
);

export default client;
